// Closing qualification checklist of a deal (B2-full foundation).
//
// The checklist is one JSONB block on `Deal.qualification` — a single column
// instead of five, following the `customFields` precedent. One block is easier
// to evolve (add a box later = edit the schema here, no migration) and keeps
// the tri-state contract in one place: every box is one of
//   unknown   — no data yet ("no data" is not a fact, never a risk signal);
//   confirmed — the fact is recorded, optionally with the recorded value;
//   missing   — the fact is known to be absent (a real gap to close).
//
// The zod schemas below are the single source of the runtime contract; the
// exported types are inferred from them, so TypeScript and the parser cannot
// drift apart. The block is parsed at every service boundary through
// `parseDealQualification`: a missing or malformed block collapses to null,
// which every consumer treats as "no data" — the same tri-state silence rule
// as `stageEnteredAt` in the risk engine.
import { z } from 'zod';

const QUALIFICATION_VALUE_MAX_CHARS = 500;

const qualificationItemSchema = z.object({
  status: z.enum(['unknown', 'confirmed', 'missing']),
  /** The recorded fact: budget range, decision deadline, criteria, a name… */
  value: z.string().max(QUALIFICATION_VALUE_MAX_CHARS).nullable(),
  /**
   * Quote or fact source backing the status. Capped so one bloated row cannot
   * inflate every pipeline read that selects the column.
   */
  evidence: z.string().max(QUALIFICATION_VALUE_MAX_CHARS).nullable().optional(),
});

/** One checklist box: status plus the recorded fact value and its source. */
export type QualificationItem = z.infer<typeof qualificationItemSchema>;

/** Statuses of one checklist box. */
export type QualificationItemStatus = QualificationItem['status'];

const dealQualificationSchema = z.object({
  need: qualificationItemSchema,
  budget: qualificationItemSchema,
  decisionMaker: qualificationItemSchema,
  decisionDeadline: qualificationItemSchema,
  selectionCriteria: qualificationItemSchema,
});

/** The five closing-checklist boxes, keyed by machine field name. */
export type DealQualification = z.infer<typeof dealQualificationSchema>;

/** Machine field names in canonical display order — the UI and signal source. */
export const QUALIFICATION_FIELDS = [
  'need',
  'budget',
  'decisionMaker',
  'decisionDeadline',
  'selectionCriteria',
] as const satisfies readonly (keyof DealQualification)[];

export type QualificationField = (typeof QUALIFICATION_FIELDS)[number];

/**
 * Parses the raw `Deal.qualification` JSONB value into the typed block.
 * Returns null when the block is absent or malformed: "no block" and "broken
 * block" both mean no data, so every box reads as unknown downstream and the
 * qualification_gaps signal stays silent. Unknown keys are stripped, so a
 * future box added to the stored shape still parses here.
 */
export function parseDealQualification(raw: unknown): DealQualification | null {
  if (raw === null || raw === undefined) return null;
  const result = dealQualificationSchema.safeParse(raw);
  return result.success ? result.data : null;
}

/**
 * Number of checklist boxes that are not confirmed (unknown or missing).
 * Both count as gaps: an unfilled box is not a recorded fact, and the closing
 * risk is exactly "the deal approaches its end without recorded facts".
 */
export function qualificationGapsCount(qualification: DealQualification): number {
  return QUALIFICATION_FIELDS.reduce(
    (count, field) => (qualification[field].status === 'confirmed' ? count : count + 1),
    0,
  );
}
